// js/render/Renderer.js
// 像素渲染器：先繪到一張低解析離屏畫布（320×192），再以 imageSmoothingEnabled=false
// 整數倍放大到螢幕 → 顆粒像素風。DPR 在最終放大層處理，保證銳利。

import { PALETTE } from './Palette.js';
import { Sprites } from './Sprites.js';

export class Renderer {
  constructor(display, cols, rows, cell) {
    this.display = display;
    this.cols = cols;
    this.rows = rows;
    this.cell = cell;
    this.W = cols * cell;
    this.H = rows * cell;

    this.buf = document.createElement('canvas');
    this.buf.width = this.W;
    this.buf.height = this.H;
    this.bctx = this.buf.getContext('2d');
    this.bctx.imageSmoothingEnabled = false;

    this.scale = 1;
    this.offX = 0;
    this.offY = 0;
    this.dpr = 1;
    this.cssW = 0;
    this.cssH = 0;
    this.portrait = false; // 直向螢幕時遊戲世界順時針轉 90° 呈現
    this.t = 0;
  }

  // 依視窗尺寸決定放大倍率與置中偏移。
  // 遊戲畫面固定 16:9 橫向；手機直向（9:16）時旋轉 90° 呈現，外部 UI（DOM）不轉。
  // 縮放以「裝置像素」取整數（CSS × DPR）：像素顆粒依然銳利，
  // 且不會因 CSS 像素 floor 而令 360~375px 闊手機的遊戲縮到剩一半。
  // padTop/padBottom：預留給 HUD（目標／提示／按鈕）的上下帶——遊戲在剩餘區域置中，
  // 保證 HUD 永遠不會遮住黎先生或公司。
  layout(cssW, cssH, dpr, padTop = 0, padBottom = 0) {
    this.dpr = dpr || 1;
    this.portrait = cssH > cssW;
    const availH = Math.max(120, cssH - padTop - padBottom);
    const dw = cssW * this.dpr, dh = availH * this.dpr;
    let sd = this.portrait
      ? Math.min(dw / this.H, dh / this.W)   // 轉 90° 後寬高互換
      : Math.min(dw / this.W, dh / this.H);
    sd = Math.max(1, Math.floor(sd));
    this.scale = sd / this.dpr;              // CSS 像素倍率（可能為小數，裝置像素仍整數）
    const gameW = (this.portrait ? this.H : this.W) * this.scale;
    const gameH = (this.portrait ? this.W : this.H) * this.scale;
    this.offX = Math.floor((cssW - gameW) / 2);
    this.offY = padTop + Math.floor((availH - gameH) / 2);
    this.cssW = cssW;
    this.cssH = cssH;
    this.display.width = Math.round(cssW * this.dpr);
    this.display.height = Math.round(cssH * this.dpr);
    this.dctx = this.display.getContext('2d');
    this.dctx.imageSmoothingEnabled = false;
  }

  // 客戶端座標 → 世界格座標（直向時反轉旋轉）
  clientToWorld(cx, cy) {
    const s = this.cell * this.scale;
    if (this.portrait) {
      // 世界順時針轉 90°：世界 +x = 螢幕向下、世界 +y = 螢幕向左
      const lx = cy - this.offY;
      const ly = (this.offX + this.H * this.scale) - cx;
      return { x: lx / s, y: ly / s };
    }
    return { x: (cx - this.offX) / s, y: (cy - this.offY) / s };
  }

  render(scene, dt) {
    this.t += dt;
    const ctx = this.bctx;
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, this.W, this.H);

    const night = scene.theme === 'night';
    const typhoon = scene.theme === 'typhoon';
    this._water(ctx, night || typhoon);
    if (typhoon) this._rain(ctx);
    this._currents(ctx, scene);
    this._wind(ctx, scene);
    for (const o of (scene.oilZones || [])) this._oil(ctx, o);
    for (const poly of (scene.terrain || [])) this._terrain(ctx, poly);
    for (const g of (scene.gates || [])) this._gate(ctx, g);
    for (const bp of (scene.bumpers || [])) this._bumper(ctx, bp);
    for (const h of (scene.hazards || [])) {
      // 紅綠燈病菌熄燈中：淡化顯示（可安全通過）
      const off = h.active === false;
      if (off) ctx.globalAlpha = 0.25;
      // 病菌脈動（呼吸感）＋大小按半徑（分裂後細菌較細）
      const size = h.r ? Math.max(0.6, Math.min(1.5, h.r / 0.65)) : 1;
      const pulse = 1 + Math.sin(this.t * 5 + h.x) * 0.08;
      this._spriteScaled(ctx, 'germ', h.x, h.y, size * pulse);
      if (off) ctx.globalAlpha = 1;
    }
    for (const c of (scene.collectibles || [])) {
      if (!c.collected) {
        const bob = Math.sin(this.t * 3 + c.x) * 0.06;
        if (night) this._glow(ctx, c.x, c.y);
        this._spriteC(ctx, c.heal ? 'lemonTea' : 'coffee', c.x, c.y + bob);
      }
    }
    const dests = scene.destinations || [];
    for (let i = 0; i < dests.length; i++) {
      const d = dests[i];
      if (night) this._glow(ctx, d.x, d.y);
      this._pulse(ctx, d.x, d.y, d.done);
      this._spriteC(ctx, 'computer', d.x, d.y);
      if (dests.length > 1) this._badge(ctx, d.x, d.y, i + 1, d.done);
    }
    if (scene.route && scene.route.points.length > 1) this._route(ctx, scene.route, scene.routeActive);
    if (scene.boat && scene.boat.trail.length > 1) this._trail(ctx, scene.boat.trail);
    this._crowdPush(ctx, scene);
    if (scene.boat && scene.boat.boostT > 0) this._boostLines(ctx, scene.boat);
    if (scene.boat) this._walker(ctx, scene.boat);
    if (scene.startMarker) this._spriteC(ctx, 'computer', scene.startMarker.x, scene.startMarker.y);

    this._present();
  }

  _present() {
    const d = this.dctx;
    d.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    d.clearRect(0, 0, this.cssW, this.cssH);
    d.imageSmoothingEnabled = false;
    const s = this.scale;
    if (this.portrait) {
      // 順時針轉 90°：起點在畫面上方、目的地在下方，直向手機由上往下趕路
      d.save();
      d.translate(this.offX + this.H * s, this.offY);
      d.rotate(Math.PI / 2);
      d.drawImage(this.buf, 0, 0, this.W, this.H, 0, 0, this.W * s, this.H * s);
      d.restore();
    } else {
      d.drawImage(this.buf, 0, 0, this.W, this.H, this.offX, this.offY, this.W * s, this.H * s);
    }
  }

  _water(ctx, night = false) {
    // 柏油路面（夜間：更深色＋濃暗角）
    ctx.fillStyle = night ? '#14151c' : PALETTE[4];
    ctx.fillRect(0, 0, this.W, this.H);
    // 路緣暗角
    ctx.fillStyle = night ? '#0c0d12' : PALETTE[3];
    ctx.fillRect(0, 0, this.W, night ? 8 : 4);
    ctx.fillRect(0, this.H - (night ? 8 : 4), this.W, night ? 8 : 4);
    // 行人路格線
    ctx.fillStyle = night ? '#1e202a' : PALETTE[5];
    for (let y = 8; y < this.H - 4; y += 24) ctx.fillRect(0, y, this.W, 1);
    for (let x = 8; x < this.W; x += 32) ctx.fillRect(x, 0, 1, this.H);
    // 遠處行人微粒（流動感）
    ctx.fillStyle = night ? '#262935' : PALETTE[6];
    for (let i = 0; i < 7; i++) {
      const y = ((this.t * 7 + i * 28) % (this.H + 16)) - 8;
      for (let x = (i % 2) * 4; x < this.W; x += 14) {
        const off = Math.round(Math.sin((x + this.t * 18) * 0.12) * 1);
        ctx.fillRect(x, y + off, 3, 1);
      }
    }
  }

  // 颱風：斜落雨絲＋隨機閃電（畫面短暫泛白）
  _rain(ctx) {
    ctx.fillStyle = '#aebfd4';
    for (let i = 0; i < 42; i++) {
      const seed = (i * 97) % this.W;
      const x = (seed + this.t * 260) % (this.W + 30) - 15;
      const y = ((i * 53 + this.t * 420) % (this.H + 20)) - 10;
      ctx.fillRect(x, y, 1, 5);
      ctx.fillRect(x - 2, y - 2, 1, 4);
    }
    const flash = Math.sin(this.t * 0.7) > 0.996 || Math.sin(this.t * 2.3 + 1) > 0.9975;
    if (flash) {
      ctx.fillStyle = 'rgba(255,255,255,0.18)';
      ctx.fillRect(0, 0, this.W, this.H);
    }
  }

  // 夜間街燈光圈（罩住電腦／咖啡）
  _glow(ctx, x, y) {
    const cx = x * this.cell, cy = y * this.cell;
    ctx.save();
    ctx.fillStyle = '#f4d35e';
    ctx.globalAlpha = 0.09;
    ctx.beginPath(); ctx.arc(cx, cy, 2.6 * this.cell, 0, Math.PI * 2); ctx.fill();
    ctx.globalAlpha = 0.12;
    ctx.beginPath(); ctx.arc(cx, cy, 1.4 * this.cell, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  }

  // 人潮區（黃色火柴人＝流動人群，飄移方向＝推進方向；vortex＝迴旋人潮）
  _currents(ctx, scene) {
    for (const z of scene.currents || []) {
      if (z.vortex) { this._vortex(ctx, z.vortex); continue; }
      const x = z.x * this.cell, y = z.y * this.cell, w = z.w * this.cell, h = z.h * this.cell;
      ctx.fillStyle = 'rgba(244,211,94,0.13)';
      ctx.fillRect(x, y, w, h);
      const ang = Math.atan2(z.fy, z.fx);
      const sp = Math.hypot(z.fx, z.fy);
      const flow = (this.t * sp * 10) % 26;
      ctx.fillStyle = PALETTE[7];
      for (let yy = y + 10; yy < y + h; yy += 16) {
        for (let xx = x - 16 + flow; xx < x + w; xx += 26) {
          if (xx < x - 2 || xx > x + w + 2) continue;
          this._stickman(ctx, xx, yy, ang);
        }
      }
    }
  }

  // 迴旋人潮：虛線圓環＋三個火柴人繞圈行
  _vortex(ctx, v) {
    const cx = v.cx * this.cell, cy = v.cy * this.cell, r = v.r * this.cell;
    ctx.save();
    ctx.strokeStyle = 'rgba(244,211,94,0.35)';
    ctx.setLineDash([4, 4]);
    ctx.lineDashOffset = -this.t * 14 * Math.sign(v.s);
    ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = PALETTE[7];
    for (let i = 0; i < 3; i++) {
      const a = this.t * v.s * 0.5 + (i * Math.PI * 2) / 3;
      this._stickman(ctx, cx + Math.cos(a) * r * 0.6, cy + Math.sin(a) * r * 0.6, a + (Math.PI / 2) * Math.sign(v.s));
    }
    ctx.restore();
  }

  // 油漬區（暗紫黑＋反光斜紋）
  _oil(ctx, z) {
    const x = z.x * this.cell, y = z.y * this.cell, w = z.w * this.cell, h = z.h * this.cell;
    ctx.fillStyle = 'rgba(10,8,20,0.55)';
    ctx.fillRect(x, y, w, h);
    ctx.fillStyle = 'rgba(120,90,200,0.18)';
    ctx.fillRect(x + 2, y + 2, w - 4, h - 4);
    ctx.strokeStyle = 'rgba(200,190,255,0.25)';
    ctx.lineWidth = 1;
    for (let i = 6; i < w + h; i += 14) {
      ctx.beginPath();
      ctx.moveTo(x + Math.max(0, i - h), y + Math.min(i, h));
      ctx.lineTo(x + Math.min(i, w), y + Math.max(0, i - w));
      ctx.stroke();
    }
  }

  // 彈彈椅：深色圓座＋青色環＋白高光（脈動）
  _bumper(ctx, bp) {
    const cx = bp.x * this.cell, cy = bp.y * this.cell;
    const r = bp.r * this.cell * (1 + Math.sin(this.t * 4 + bp.x) * 0.05);
    ctx.fillStyle = '#1d3557';
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = PALETTE[18]; ctx.lineWidth = 3;
    ctx.stroke();
    ctx.fillStyle = PALETTE[19];
    ctx.beginPath(); ctx.arc(cx - r * 0.3, cy - r * 0.3, r * 0.25, 0, Math.PI * 2); ctx.fill();
  }

  // 簡約火柴人（人潮粒子）：看板式保持正向，並向流向前傾
  _stickman(ctx, x, y, ang) {
    ctx.save();
    ctx.translate(Math.round(x), Math.round(y));
    if (this.portrait) ctx.rotate(-Math.PI / 2);
    ctx.rotate(Math.cos(ang) * 0.3);  // 東流右傾、西流左傾
    ctx.fillRect(-1, -5, 2, 2);       // 頭
    ctx.fillRect(0, -3, 1, 4);        // 身
    ctx.fillRect(-2, -2, 5, 1);       // 手臂
    ctx.fillRect(-2, 1, 2, 1);        // 左腿
    ctx.fillRect(1, 1, 2, 1);         // 右腿
    ctx.restore();
  }

  // 人潮推力視覺：黎先生身處人潮區時，火柴人由後方湧向推力方向（順流／逆流一目了然）
  _crowdPush(ctx, scene) {
    const b = scene.boat;
    if (!b) return;
    const zones = [...(scene.currents || []), ...(scene.windZones || [])];
    let fx = 0, fy = 0, inZone = false;
    for (const z of zones) {
      if (b.x >= z.x && b.x <= z.x + z.w && b.y >= z.y && b.y <= z.y + z.h) {
        fx += z.fx; fy += z.fy; inZone = true;
      }
    }
    if (!inZone) return;
    const ang = Math.atan2(fy, fx);
    const wx = b.x * this.cell, wy = b.y * this.cell;
    ctx.save();
    ctx.fillStyle = PALETTE[7];
    for (let i = 0; i < 3; i++) {
      const ph = (this.t * 1.6 + i / 3) % 1;   // 循環相位
      const dist = 7 + (1 - ph) * 17;          // 由遠處湧向角色
      ctx.globalAlpha = 0.25 + ph * 0.6;       // 愈近愈實
      this._stickman(ctx, wx - Math.cos(ang) * dist, wy - Math.sin(ang) * dist, ang);
    }
    ctx.restore();
  }

  // 側向人潮（白色微粒流動）
  _wind(ctx, scene) {
    ctx.save();
    ctx.fillStyle = PALETTE[19];
    for (const z of scene.windZones || []) {
      const x = z.x * this.cell, y = z.y * this.cell, w = z.w * this.cell, h = z.h * this.cell;
      ctx.globalAlpha = 0.45;
      for (let i = 0; i < 12; i++) {
        const seed = i * 53.71 + z.x * 7;
        let px = x + (((seed * 13 + this.t * z.fx * 26) % w) + w) % w;
        let py = y + (((seed * 7 + this.t * z.fy * 26) % h) + h) % h;
        const ang = Math.atan2(z.fy, z.fx);
        ctx.save();
        ctx.translate(Math.round(px), Math.round(py));
        ctx.rotate(ang);
        ctx.fillRect(-2, 0, 4, 1);
        ctx.restore();
      }
    }
    ctx.restore();
  }

  // 工地（泥土色多邊形）
  _terrain(ctx, poly) {
    const c = this.cell;
    ctx.beginPath();
    poly.forEach((p, i) => (i ? ctx.lineTo(p.x * c, p.y * c) : ctx.moveTo(p.x * c, p.y * c)));
    ctx.closePath();
    ctx.fillStyle = PALETTE[9];   // 泥地
    ctx.fill();
    // 內層（略小的多邊形頂點向重心收）
    let gx = 0, gy = 0;
    for (const p of poly) { gx += p.x; gy += p.y; }
    gx /= poly.length; gy /= poly.length;
    ctx.beginPath();
    poly.forEach((p, i) => {
      const ix = gx + (p.x - gx) * 0.82, iy = gy + (p.y - gy) * 0.82;
      i ? ctx.lineTo(ix * c, iy * c) : ctx.moveTo(ix * c, iy * c);
    });
    ctx.closePath();
    ctx.fillStyle = PALETTE[10];  // 沙
    ctx.fill();
    // 圍板描邊
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2;
    ctx.strokeStyle = PALETTE[1];
    ctx.beginPath();
    poly.forEach((p, i) => (i ? ctx.lineTo(p.x * c, p.y * c) : ctx.moveTo(p.x * c, p.y * c)));
    ctx.closePath();
    ctx.stroke();
  }

  // 閘機：關閉＝實心藍灰閘條＋斜紋；開啟＝淡化虛線（可通過）
  _gate(ctx, g) {
    const c = this.cell;
    const poly = g.poly;
    const closed = g.active !== false;
    ctx.save();
    ctx.globalAlpha = closed ? 1 : 0.22;
    ctx.beginPath();
    poly.forEach((p, i) => (i ? ctx.lineTo(p.x * c, p.y * c) : ctx.moveTo(p.x * c, p.y * c)));
    ctx.closePath();
    ctx.fillStyle = '#3d5a80';
    ctx.fill();
    if (closed) {
      // 斜紋（閘機感）
      ctx.save();
      ctx.clip();
      ctx.strokeStyle = '#98c1d9';
      ctx.lineWidth = 3;
      let gx = 0, gy = 0;
      for (const p of poly) { gx += p.x; gy += p.y; }
      gx /= poly.length; gy /= poly.length;
      for (let i = -24; i < 48; i += 8) {
        ctx.beginPath();
        ctx.moveTo((gx * c) + i - 20, (gy * c) - 40);
        ctx.lineTo((gx * c) + i + 20, (gy * c) + 40);
        ctx.stroke();
      }
      ctx.restore();
    }
    ctx.globalAlpha = closed ? 1 : 0.5;
    ctx.setLineDash(closed ? [] : [4, 3]);
    ctx.lineWidth = 2;
    ctx.strokeStyle = closed ? '#0d1b2a' : '#98c1d9';
    ctx.beginPath();
    poly.forEach((p, i) => (i ? ctx.lineTo(p.x * c, p.y * c) : ctx.moveTo(p.x * c, p.y * c)));
    ctx.closePath();
    ctx.stroke();
    ctx.restore();
  }

  // 精靈以「看板」方式呈現：直向時反向補償 −90°，人頭／電腦保持正向不橫躺
  _spriteC(ctx, name, x, y) {
    this._spriteScaled(ctx, name, x, y, 1);
  }

  _spriteScaled(ctx, name, x, y, scale) {
    const s = Sprites[name];
    if (!s) return;
    ctx.save();
    ctx.translate(Math.round(x * this.cell), Math.round(y * this.cell));
    if (this.portrait) ctx.rotate(-Math.PI / 2);
    if (scale !== 1) ctx.scale(scale, scale);
    ctx.drawImage(s, -8, -8);
    ctx.restore();
  }

  _pulse(ctx, x, y, done = false) {
    const r = (0.55 + Math.sin(this.t * 4) * 0.18) * this.cell;
    ctx.save();
    ctx.globalAlpha = done ? 0.10 : 0.25;
    ctx.fillStyle = PALETTE[17];
    ctx.beginPath();
    ctx.arc(x * this.cell, y * this.cell, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // 多站徽章：右上角小圓＋像素數字（完成→綠，未到→金）
  _badge(ctx, x, y, num, done) {
    const bx = x * this.cell + 7, by = y * this.cell - 8;
    const DIGITS = {
      1: ['010', '110', '010', '010', '111'],
      2: ['111', '001', '111', '100', '111'],
      3: ['111', '001', '011', '001', '111'],
    };
    const rows = DIGITS[num];
    if (!rows) return;
    ctx.save();
    ctx.fillStyle = done ? PALETTE[18] : PALETTE[17];
    ctx.fillRect(bx - 3, by - 3, 9, 11);
    ctx.fillStyle = '#000';
    for (let r = 0; r < 5; r++) {
      for (let c = 0; c < 3; c++) {
        if (rows[r][c] === '1') ctx.fillRect(bx + c, by + r, 1, 1);
      }
    }
    ctx.restore();
  }

  // 咖啡因衝刺：角色兩側白色速度線（漫畫風）
  _boostLines(ctx, boat) {
    const bx = boat.x * this.cell, by = boat.y * this.cell;
    const ph = (this.t * 20) % 6;
    ctx.save();
    ctx.fillStyle = PALETTE[19];
    ctx.globalAlpha = 0.7;
    for (let i = 0; i < 3; i++) {
      const off = 5 + ((ph + i * 2) % 6);
      ctx.fillRect(bx - off - 4, by - 4 + i * 3, 4, 1);
      ctx.fillRect(bx + off, by - 4 + i * 3, 4, 1);
    }
    ctx.restore();
  }

  _route(ctx, route, active) {
    const c = this.cell;
    ctx.save();
    ctx.strokeStyle = active ? PALETTE[19] : PALETTE[15];
    ctx.lineWidth = 2;
    ctx.setLineDash([3, 3]);
    ctx.lineDashOffset = active ? -(this.t * 8) : 0;
    ctx.beginPath();
    route.points.forEach((p, i) => (i ? ctx.lineTo(p.x * c, p.y * c) : ctx.moveTo(p.x * c, p.y * c)));
    ctx.stroke();
    ctx.restore();
  }

  _trail(ctx, trail) {
    const c = this.cell;
    ctx.save();
    ctx.strokeStyle = PALETTE[14];
    ctx.lineWidth = 2;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.beginPath();
    trail.forEach((p, i) => (i ? ctx.lineTo(p.x * c, p.y * c) : ctx.moveTo(p.x * c, p.y * c)));
    ctx.stroke();
    ctx.restore();
  }

  _walker(ctx, walker) {
    const cx = walker.x * this.cell, cy = walker.y * this.cell;
    ctx.save();
    ctx.translate(cx, cy);
    // 人頭永遠正向（不隨航向打轉）；直向時補償畫面旋轉
    if (this.portrait) ctx.rotate(-Math.PI / 2);
    ctx.drawImage(Sprites.walker, -8, -8);
    ctx.restore();
  }
}
